import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

interface TenantRow { id: number; name: string; company_name?: string; }
interface UserRow { id: number; username: string; role: string; tenant_id: number; display_name?: string; is_admin?: any; }

// Platform (TEC) admin tool: grant an admin the ability to manage OTHER tenants.
// Once granted, that admin gets a tenant switcher at the top of the console and
// can administer each ticked tenant exactly like their own.
export default function ManagedTenantsPage(_props: { user?: any }) {
  const [tenants, setTenants] = useState<TenantRow[]>([]);
  const [users, setUsers] = useState<UserRow[]>([]);
  const [sel, setSel] = useState<number | null>(null);
  const [checked, setChecked] = useState<Set<number>>(new Set());
  const [q, setQ] = useState('');
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true); const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const [t, u] = await Promise.all([
        axios.get('/api/admin-panel/all-tenants'),
        axios.get('/api/admin-panel/users'),
      ]);
      setTenants(t.data?.tenants || []);
      const list = Array.isArray(u.data) ? u.data : (u.data?.users || []);
      setUsers(list);
    } catch (e: any) { setErr(e?.response?.data?.error || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const pickUser = async (id: number) => {
    setSel(id); setMsg(''); setErr('');
    try {
      const r = await axios.get(`/api/admin-panel/users/${id}/managed-tenants`);
      setChecked(new Set((r.data?.managed_tenant_ids || []).map(Number)));
    } catch (e: any) { setErr(e?.response?.data?.error || 'Could not load this user'); }
  };

  const toggle = (id: number) => { const s = new Set(checked); s.has(id) ? s.delete(id) : s.add(id); setChecked(s); };

  const save = async () => {
    if (sel == null) return;
    setSaving(true); setMsg(''); setErr('');
    try {
      await axios.put(`/api/admin-panel/users/${sel}/managed-tenants`, { tenant_ids: Array.from(checked) });
      setMsg('Saved. The user can now switch to and manage the ticked tenants (they must sign in again for it to take effect).');
    } catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;

  const uMatch = (u: UserRow) => (u.username || '').toLowerCase().includes(q.toLowerCase());
  const shownUsers = users.filter((u) => uMatch(u) && (u.role === 'tenant_admin' || u.role === 'super_admin' || !!u.is_admin));
  const selUser = users.find((u) => u.id === sel);

  const wrap: React.CSSProperties = { padding: 24, maxWidth: 1040 };
  const btn: React.CSSProperties = { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const input: React.CSSProperties = { padding: 10, border: '1px solid #ddd', borderRadius: 8, fontSize: 14 };
  const chip = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid ' + (on ? '#6366f1' : '#ddd'), borderRadius: 8, background: on ? '#eef0ff' : '#fff', cursor: 'pointer', fontSize: 14 });

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px' }}>Managed Tenants</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Grant an admin the ability to manage other tenants. Once granted, that admin sees a tenant
        switcher at the top of the console and can administer each ticked tenant exactly like their own.
        Changes take effect the next time the user signs in.
      </p>
      {err && <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 8, marginBottom: 10 }}>{msg}</div>}

      <div style={{ display: 'flex', gap: 20 }}>
        <div style={{ minWidth: 240 }}>
          <input placeholder="Search admins…" value={q} onChange={(e) => setQ(e.target.value)} style={{ ...input, width: '100%', marginBottom: 8 }} />
          <div style={{ maxHeight: 520, overflowY: 'auto', border: '1px solid #eee', borderRadius: 8 }}>
            {shownUsers.map((u) => (
              <div key={u.id} onClick={() => pickUser(u.id)}
                style={{ padding: '9px 12px', cursor: 'pointer', background: sel === u.id ? '#eef0ff' : 'transparent', borderBottom: '1px solid #f5f5f5' }}>
                <div style={{ fontWeight: sel === u.id ? 700 : 500, fontSize: 14 }}>{u.username}</div>
                <div style={{ fontSize: 12, color: '#888' }}>{u.role} · tenant {u.tenant_id}</div>
              </div>
            ))}
            {shownUsers.length === 0 && <div style={{ padding: 14, color: '#999', fontSize: 13 }}>No admins match.</div>}
          </div>
        </div>

        <div style={{ flex: 1 }}>
          {sel == null ? <div style={{ color: '#999', padding: 20 }}>Select an admin to grant tenant management.</div> : (
            <div>
              <div style={{ marginBottom: 12, fontWeight: 600 }}>
                Tenants {selUser ? <span style={{ color: '#666', fontWeight: 400 }}>· {selUser.username} always manages their home tenant (#{selUser.tenant_id}); tick any extra tenants below</span> : null}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))', gap: 8 }}>
                {tenants.map((t) => {
                  const isHome = !!selUser && Number(t.id) === Number(selUser.tenant_id);
                  const on = checked.has(Number(t.id)) || isHome;
                  return (
                    <div key={t.id} style={{ ...chip(on), opacity: isHome ? 0.6 : 1, cursor: isHome ? 'default' : 'pointer' }}
                      onClick={() => { if (!isHome) toggle(Number(t.id)); }}>
                      <input type="checkbox" checked={on} readOnly disabled={isHome} style={{ width: 'auto', margin: 0 }} />
                      <span>{t.company_name || t.name} <span style={{ color: '#999' }}>#{t.id}{isHome ? ' · home' : ''}</span></span>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 18 }}>
                <button style={{ ...btn, opacity: saving ? 0.6 : 1 }} disabled={saving} onClick={save}>
                  {saving ? 'Saving…' : 'Save managed tenants'}
                </button>
                <span style={{ marginLeft: 12, color: '#666', fontSize: 13 }}>{checked.size} extra tenant(s)</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
